import React, { useEffect, useRef, useContext, useLayoutEffect, useMemo } from "react";
import { Viewer, ImageryLayer } from "resium";
import { Cartesian3, Rectangle, Camera, Entity as cesiumEntity, SingleTileImageryProvider } from "cesium";
import "cesium/Source/Widgets/widgets.css";

import { RendouFunc, SyukurenFunc, FullscreenFunc } from "../cesiumfunction";
import {
  FullviewerSet,
  RendouSet,
  Map1Set,
  Map2Set,
  Zoomlevel1Set,
  Zoomlevel2Set,
  Static2Map1Set,
  Static2Map2Set,
  Rotate1Set,
  Rotate2Set,
  Area1Set,
  Area2Set,
  Cesium1Set,
  Cesium2Set,
  Color1Set,
  Color2Set,
  Shikii1Set,
  Shikii2Set,
  Ratio1Set,
  Ratio2Set,
  Year1Set,
  Year2Set,
  Alpha1Set,
  Alpha2Set,
  Zoomhanrei1Set,
  Zoomhanrei2Set,
  ModeSet,
  PromodeSet,
} from "../App";

import { lod } from "../kashikasetting";
import { FuncOnloadZoomTenkei } from "./funcOnloadZoomTenkei";
import { AddCesiumNavigation } from "./addCesiumNavigation";
import { FunkClickFlyto } from "./funcClickFlyto";
import { csvfecth } from "../coreKashika/csvFetch";
import { FuncRotate } from "./funcRotate";
import { csvfecthforcheck } from "../coreKashika/csvFetchforcheck";
import { AddGoogleMapsApi } from "./addGoogleMapsApi";
import { AddPlateauApiChiyoda } from "./addPlateauApiChiyoda";
import { FuncZoomLatLon } from "./funcZoomLatLon";
import { FunkClickPopupInfo } from "./funcClickPopupInfo";

// =========================
// 共通ユーティリティ
// =========================

const DEBUG = true;

// 年キー
const YEAR_KEYS_15 = Array.from({ length: 15 }, (_, i) => `year${i + 1}`);
const YEAR_KEYS_10 = Array.from({ length: 10 }, (_, i) => `year${i + 1}`);

const getViewer = (rfcs) => rfcs?.current?.cesiumElement;

const normalizeNFD = (v, fallback) => {
  if (!v) return fallback;
  try {
    return v.normalize("NFD");
  } catch {
    return v;
  }
};

const hideCredit = (viewer) => {
  try {
    const el = viewer?._cesiumWidget?._creditContainer;
    if (el) el.style.display = "none";
  } catch {
    // 無視（内部実装差分で落ちないように）
  }
};

const ensureYearEntities = (viewer, dataRef) => {
  if (!viewer) return;

  const needsRebuild =
    !dataRef.current ||
    YEAR_KEYS_15.some((k) => {
      const e = dataRef.current?.[k];
      return !e || !viewer.entities.contains(e);
    });

  if (!needsRebuild) return;

  const map = {};
  YEAR_KEYS_15.forEach((k) => {
    map[k] = viewer.entities.add(new cesiumEntity());
  });

  // 初期表示は year5（現状維持）
  YEAR_KEYS_15.forEach((k) => {
    map[k].show = k === "year5";
  });

  dataRef.current = map;
};

function buildMetaOverride(meta, colorSet, shikiiSet) {
  const c = Array.isArray(colorSet) ? colorSet : [];
  const s = Array.isArray(shikiiSet) ? shikiiSet : [];

  // 色は「空文字」は未指定扱い（=元metaを使う）
  const pickColor = (i, fallback) => {
    const v = c[i];
    return v != null && v !== "" ? v : fallback;
  };

  return {
    ...meta,

    // KML style 用（存在していれば上書き）
    kml1_color: pickColor(0, meta?.kml1_color),
    kml2_color: pickColor(1, meta?.kml2_color),
    kml3_color: pickColor(2, meta?.kml3_color),
    kml4_color: pickColor(3, meta?.kml4_color),
    kml5_color: pickColor(4, meta?.kml5_color),

    // 凡例色
    hanrei1_color: pickColor(0, meta?.hanrei1_color),
    hanrei2_color: pickColor(1, meta?.hanrei2_color),
    hanrei3_color: pickColor(2, meta?.hanrei3_color),
    hanrei4_color: pickColor(3, meta?.hanrei4_color),
    hanrei5_color: pickColor(4, meta?.hanrei5_color),

    // 閾値（shikii1..5）
    shikii1: s[0] ?? meta?.shikii1,
    shikii2: s[1] ?? meta?.shikii2,
    shikii3: s[2] ?? meta?.shikii3,
    shikii4: s[3] ?? meta?.shikii4,
    shikii5: s[4] ?? meta?.shikii5,
  };
}

const colorFromMeta = (m) =>
  [
    m?.kml1_color ?? m?.hanrei1_color,
    m?.kml2_color ?? m?.hanrei2_color,
    m?.kml3_color ?? m?.hanrei3_color,
    m?.kml4_color ?? m?.hanrei4_color,
    m?.kml5_color ?? m?.hanrei5_color,
  ].map((v) => (v == null ? "" : String(v)));

const shikiiFromMeta = (m) => [m?.shikii1, m?.shikii2, m?.shikii3, m?.shikii4, m?.shikii5];

/**
 * ★「初回だけ year5」ロジック込み（現状維持）
 * - selectedYearValue が未確定（"" / 0 / undefined 等）でも初回は year5 を表示
 * - ただし一度でも有効な年が選ばれたら、その後は last を優先
 * - 3000 は常に非表示
 */
const applyYearVisibility = (viewer, dataRef, metadata, selectedYearValue, lastKeyRef) => {
  if (!viewer || !dataRef.current || !metadata) return;

  const yearSetMapping = Object.create(null);
  YEAR_KEYS_15.forEach((k) => {
    const v = metadata[k];
    if (v === undefined || v === null) return;
    yearSetMapping[String(v)] = k;
  });

  yearSetMapping["3000"] = null;

  const sv = selectedYearValue;
  const svStr = sv === undefined || sv === null ? "" : String(sv);

  let targetKey = yearSetMapping[svStr];

  if ((svStr === "" || svStr === "0") && !targetKey) {
    targetKey = lastKeyRef?.current ? lastKeyRef.current : "year5";
  }

  if (targetKey && lastKeyRef) {
    lastKeyRef.current = targetKey;
  }

  YEAR_KEYS_15.forEach((k) => {
    const ent = dataRef.current[k];
    if (ent) ent.show = targetKey ? k === targetKey : false;
  });

  if (DEBUG) {
    try {
      console.log("[YearSwitch Debug]", {
        selectedYearValue,
        entities: viewer.entities.values.length,
        primitives: viewer.scene?.primitives?.length,
        dataSources: viewer.dataSources?.length,
      });
    } catch (e) {
      console.warn("YearSwitch debug failed", e);
    }
  }
};

const loadCsvSeries = ({
  rfcs,
  dataRef,
  metadata,
  area,
  ratio,
  alpha,
  color,
  shikii,
  csvNameFallback = "人口総数×人口総数",
  doFileCheckAtYear = 5,
  years = YEAR_KEYS_10,
  nihonsokuchiRule = (md, i) => {
    const yearKey = `year${i}`;
    return i <= 8 ? (md?.[`${yearKey}_nihonsokuchi`] || 0) * (md?.[yearKey] || 0) : 0;
  },
}) => {
  const viewer = getViewer(rfcs);
  if (!viewer || !metadata) return;

  ensureYearEntities(viewer, dataRef);

  const csv_name = normalizeNFD(metadata?.csv_filename, csvNameFallback);
  const areaN = normalizeNFD(area, "福岡県糸島市");

  if (DEBUG) {
    console.log("[loadCsvSeries]", {
      metaNo: metadata?.no,
      areaIn: area,
      areaN,
      csv_name,
    });
  }

  years.forEach((yearKey) => {
    const i = Number(yearKey.replace("year", ""));
    const yearNum = metadata?.[yearKey];
    if (!yearNum) return;

    csvfecth(
      rfcs,
      dataRef.current[yearKey],
      metadata.no,
      yearNum,
      areaN,
      csv_name,
      nihonsokuchiRule(metadata, i),
      metadata,
      ratio,
      alpha,
      color,
      shikii,
      i === 1 ? yearNum : 0
    );

    if (i === doFileCheckAtYear) {
      csvfecthforcheck(
        rfcs,
        dataRef.current[yearKey],
        metadata.no,
        yearNum,
        areaN,
        csv_name,
        0,
        metadata,
        ratio,
        alpha,
        color,
        shikii
      );
    }
  });
};

const useMoveEndZoomReset = ({
  rfcs,
  isEnabled,
  modeset,
  lod,
  rendouset,
  setZoomlevelSelf,
  setZoomhanreiSelf,
  setZoomlevelOther,
  setZoomhanreiOther,
}) => {
  useEffect(() => {
    if (!isEnabled) return;

    const viewer = getViewer(rfcs);
    if (!viewer?.camera?.moveEnd) return;

    const handler = () => {
      const height = viewer.camera.positionCartographic?.height ?? 0;
      const threshold = modeset == 5 ? lod?.[1] : lod?.[4];

      if (threshold != null && height >= threshold) {
        setZoomlevelSelf(1);
        setZoomhanreiSelf(null);

        if (rendouset) {
          setZoomlevelOther(1);
          setZoomhanreiOther(null);
        }
      }
    };

    viewer.camera.moveEnd.addEventListener(handler);
    return () => {
      try {
        viewer.camera.moveEnd.removeEventListener(handler);
      } catch {
        //
      }
    };
  }, [
    rfcs,
    isEnabled,
    modeset,
    rendouset,
    lod,
    setZoomlevelSelf,
    setZoomhanreiSelf,
    setZoomlevelOther,
    setZoomhanreiOther,
  ]);
};

// =========================
// blank imagery provider（固定）
// =========================
const BLANK_IMAGE_URI =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAkQBADs=";

const BLANK_IMAGERY_PROVIDER = new SingleTileImageryProvider({ url: BLANK_IMAGE_URI });

// =========================
// 本体
// =========================
const position = Cartesian3.fromDegrees(135.0707383, 35.7117244, 100);

export const CesiumContainer = (props) => {
  // location.search を安全に評価（現状維持）
  let locationsetting = [];
  try {
    locationsetting = decodeURI(window.location.search || "").split("_");
  } catch {
    locationsetting = String(window.location.search || "").split("_");
  }
  if (DEBUG) console.log(locationsetting);

  // Camera初期範囲（現状維持）
  const extent = Rectangle.fromDegrees(129.9999999999, -89.99999999, 130, -89.999999999);
  Camera.DEFAULT_VIEW_RECTANGLE = extent;

  const rfcs1 = useRef(null);
  const rfcs2 = useRef(null);

  // year entity map
  const data1Ref = useRef(null);
  const data2Ref = useRef(null);

  // ★「初回だけ year5」用
  const lastYearKey1Ref = useRef("year5");
  const lastYearKey2Ref = useRef("year5");

  // Contexts
  const { setCesium1Set } = useContext(Cesium1Set);
  const { setCesium2Set } = useContext(Cesium2Set);

  const { fullviewerset } = useContext(FullviewerSet);
  const { rendouset } = useContext(RendouSet);

  const { map1set } = useContext(Map1Set);
  const { map2set } = useContext(Map2Set);

  const { setZoomlevel1Set } = useContext(Zoomlevel1Set);
  const { setZoomlevel2Set } = useContext(Zoomlevel2Set);
  const { setZoomhanrei1Set } = useContext(Zoomhanrei1Set);
  const { setZoomhanrei2Set } = useContext(Zoomhanrei2Set);

  const { static2map1set } = useContext(Static2Map1Set);
  const { static2map2set } = useContext(Static2Map2Set);

  const { rotate1set } = useContext(Rotate1Set);
  const { rotate2set } = useContext(Rotate2Set);

  const { area1set } = useContext(Area1Set);
  const { area2set } = useContext(Area2Set);

  const { ratio1set } = useContext(Ratio1Set);
  const { ratio2set } = useContext(Ratio2Set);

  const { year1set } = useContext(Year1Set);
  const { year2set } = useContext(Year2Set);

  const { alpha1set } = useContext(Alpha1Set);
  const { alpha2set } = useContext(Alpha2Set);

  const { color1set } = useContext(Color1Set);
  const { color2set } = useContext(Color2Set);

  const { shikii1set } = useContext(Shikii1Set);
  const { shikii2set } = useContext(Shikii2Set);

  const { modeset } = useContext(ModeSet);
  const { promodeset } = useContext(PromodeSet)

  // const meta1 = useMemo(() => {
  //   if (!static2map1set) return null;
  //   return buildMetaOverride(static2map1set, color1set, shikii1set);
  // }, [static2map1set, color1set, shikii1set]);

  // const meta2 = useMemo(() => {
  //   if (!static2map2set) return null;
  //   return buildMetaOverride(static2map2set, color2set, shikii2set);
  // }, [static2map2set, color2set, shikii2set]);

  const meta1 = useMemo(() => {
    if (!static2map1set) return null;

    // promode OFF → overrideしない（元のmetaを使う）
    if (!promodeset) return static2map1set;

    // promode ON → override
    return buildMetaOverride(static2map1set, color1set, shikii1set);
  }, [static2map1set, color1set, shikii1set, promodeset]);

  const meta2 = useMemo(() => {
    if (!static2map2set) return null;

    if (!promodeset) return static2map2set;

    return buildMetaOverride(static2map2set, color2set, shikii2set);
  }, [static2map2set, color2set, shikii2set, promodeset]);

  useEffect(() => {
    console.log("[AREA DEBUG]", {
      area1set,
      area2set,
      time: performance.now()
    });
  }, [area1set, area2set]);

  // credit非表示（初回だけ）
  useLayoutEffect(() => {
    const v1 = getViewer(rfcs1);
    const v2 = getViewer(rfcs2);
    if (v1) hideCredit(v1);
    if (v2) hideCredit(v2);
  }, []);

  // App側へ ref を渡す（初回のみ）
  useEffect(() => {
    setCesium1Set(rfcs1);
    setCesium2Set(rfcs2);
  }, [setCesium1Set, setCesium2Set]);

  // moveEnd（ズームしきい値で凡例/ズームレベルを戻す）
  useMoveEndZoomReset({
    rfcs: rfcs1,
    isEnabled: true,
    modeset,
    lod,
    rendouset,
    setZoomlevelSelf: setZoomlevel1Set,
    setZoomhanreiSelf: setZoomhanrei1Set,
    setZoomlevelOther: setZoomlevel2Set,
    setZoomhanreiOther: setZoomhanrei2Set,
  });

  useMoveEndZoomReset({
    rfcs: rfcs2,
    isEnabled: true,
    modeset,
    lod,
    rendouset,
    setZoomlevelSelf: setZoomlevel2Set,
    setZoomhanreiSelf: setZoomhanrei2Set,
    setZoomlevelOther: setZoomlevel1Set,
    setZoomhanreiOther: setZoomhanrei1Set,
  });

  // year entity 初期生成（左右まとめて。依存は空＝初回のみ、挙動維持）
  useLayoutEffect(() => {
    const v1 = getViewer(rfcs1);
    const v2 = getViewer(rfcs2);
    if (v1) ensureYearEntities(v1, data1Ref);
    if (v2) ensureYearEntities(v2, data2Ref);
  }, []);

  // ★ 検証用：style/param 変更時も「全部」removeAll（左）※挙動維持
  useEffect(() => {
    const viewer = getViewer(rfcs1);
    if (!viewer) return;

    if (DEBUG) {
      console.log("[removeAll TEST] LEFT changed:", {
        area1set,
        ratio1set,
        alpha1set,
        color1set,
        shikii1set,
      });
    }

    try {
      viewer.entities.removeAll();
    } catch (e) {
      console.warn("[removeAll TEST] LEFT removeAll failed:", e);
    }

    data1Ref.current = null;
    ensureYearEntities(viewer, data1Ref);
    // ★ ここが復元ポイント：再構築直後に「今のyear」を反映して year5 を消す
    if (meta1) applyYearVisibility(viewer, data1Ref, meta1, year1set, lastYearKey1Ref);

  }, [area1set, ratio1set, alpha1set, color1set, shikii1set, promodeset]);

  // ★ 検証用：area2 変更時に「全部」removeAll（右）※挙動維持
  // ★ rfcs2 も rfcs1 と同じ：style/param 変更時に「全部」removeAll（右）
  useEffect(() => {
    const viewer = getViewer(rfcs2);
    if (!viewer) return;

    console.log("[removeAll TEST] RIGHT changed:", {
      area2set,
      ratio2set,
      alpha2set,
      color2set,
      shikii2set,
      // 必要なら year2set も
    });

    try {
      viewer.entities.removeAll();
    } catch (e) {
      console.warn("[removeAll TEST] RIGHT removeAll failed:", e);
    }

    data2Ref.current = null;
    ensureYearEntities(viewer, data2Ref);
    // ★ 同じく：再構築直後に「今のyear」を反映
    if (meta2) applyYearVisibility(viewer, data2Ref, meta2, year2set, lastYearKey2Ref);

  }, [area2set, ratio2set, alpha2set, color2set, shikii2set, promodeset]);

  // 左：可視化（テーマ/都市/タブ切替など）
  useEffect(() => {
    const viewer = getViewer(rfcs1);
    if (!viewer) return;

    const metadata = meta1;
    if (!metadata) return;

    loadCsvSeries({
      rfcs: rfcs1,
      dataRef: data1Ref,
      metadata,
      area: area1set || "福岡県糸島市",
      ratio: ratio1set,
      alpha: alpha1set,
      color: colorFromMeta(metadata),
      shikii: shikiiFromMeta(metadata),
      years: YEAR_KEYS_10,
      doFileCheckAtYear: 5,
      nihonsokuchiRule: (md, i) => {
        const yearKey = `year${i}`;
        const yearNum = md?.[yearKey] || 0;
        return i <= 8 ? (md?.[`${yearKey}_nihonsokuchi`] || 0) * yearNum : 0;
      },
    });

    // ★removeAll は復活させない（現状維持）
    return () => {
      //
    };
  }, [meta1, area1set, ratio1set, alpha1set]);

  // 左：表示ON/OFF（year切替）
  useEffect(() => {
    const viewer = getViewer(rfcs1);
    if (!viewer) return;

    const metadata = meta1;
    if (!metadata) return;

    ensureYearEntities(viewer, data1Ref);
    applyYearVisibility(viewer, data1Ref, metadata, year1set, lastYearKey1Ref);
  }, [meta1, year1set]);

  // 右：可視化（modeset条件あり）
  useEffect(() => {
    const viewer = getViewer(rfcs2);
    if (!viewer) return;

    const metadata = meta2;
    if (!metadata) return;

    const enabled = modeset < 10;
    if (!enabled) return;

    const years = ["year1", "year2", "year3", "year4", "year5", "year6", "year7", "year8"];

    loadCsvSeries({
      rfcs: rfcs2,
      dataRef: data2Ref,
      metadata,
      area: area2set || "福岡県糸島市",
      ratio: ratio2set,
      alpha: alpha2set,
      color: colorFromMeta(metadata),
      shikii: shikiiFromMeta(metadata),
      years,
      doFileCheckAtYear: -1,
      nihonsokuchiRule: (md, i) => {
        const yearKey = `year${i}`;
        const yearNum = md?.[yearKey] || 0;
        return (md?.[`${yearKey}_nihonsokuchi`] || 0) * yearNum;
      },
    });

    // ★removeAll は復活させない（現状維持）
    return () => {
      //
    };
  }, [meta2, area2set, ratio2set, alpha2set, modeset]);

  // 右：表示ON/OFF（year切替）
  useEffect(() => {
    const viewer = getViewer(rfcs2);
    if (!viewer) return;

    const metadata = meta2;
    if (!metadata) return;

    ensureYearEntities(viewer, data2Ref);
    applyYearVisibility(viewer, data2Ref, metadata, year2set, lastYearKey2Ref);
  }, [meta2, year2set]);

  // 既存のエフェクト（現状維持）
  FullscreenFunc(rfcs1, rfcs2);
  RendouFunc(rfcs1, rfcs2);
  SyukurenFunc(rfcs1, rfcs2);

  return (
    <div className="overflow-hidden">
      {!(modeset == 7777777 || modeset == 7777778) && (
        <>
          <FuncOnloadZoomTenkei rfcs={rfcs1} rfcs2={rfcs2} />
          <FunkClickFlyto rfcs={rfcs1} defratio={static2map1set} valratio={ratio1set} />
          <FunkClickFlyto rfcs={rfcs2} defratio={static2map2set} valratio={ratio2set} />
        </>
      )}

      <FuncRotate rfcs={rfcs1} rotateset={rotate1set} />
      <FuncRotate rfcs={rfcs2} rotateset={rotate2set} />

      {modeset != 9999999 && <AddCesiumNavigation rfcs={rfcs1} />}

      {modeset == 7777777 && (
        <>
          <AddGoogleMapsApi rfcs={rfcs2} />
          <AddPlateauApiChiyoda rfcs={rfcs1} />
          <FuncZoomLatLon rfcs={rfcs1} areaset={area1set} />
          <FuncZoomLatLon rfcs={rfcs2} areaset={area1set} />
          <FunkClickPopupInfo rfcs={rfcs1} defratio={static2map1set} valratio={ratio1set} />
        </>
      )}

      {modeset == 7777778 && (
        <>
          <AddGoogleMapsApi rfcs={rfcs2} />
          <AddPlateauApiChiyoda rfcs={rfcs2} />
          <FuncZoomLatLon rfcs={rfcs1} areaset={area1set} />
          <FuncZoomLatLon rfcs={rfcs2} areaset={area2set} />
          {/* <FuncZoomLatLon rfcs={rfcs2} areaset={area1set} /> */}
          <FunkClickPopupInfo rfcs={rfcs1} defratio={static2map1set} valratio={ratio1set} />
        </>
      )}

      <Viewer
        ref={rfcs1}
        className={fullviewerset ? "z-10" : "z-10 w-1/2 ml-auto"}
        style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
        vrButton={false}
        homeButton={false}
        timeline={false}
        navigationHelpButton={false}
        fullscreenButton={true}
        baseLayerPicker={false}
        geocoder={false}
        imageryProvider={BLANK_IMAGERY_PROVIDER}
        scene3DOnly={true}
        animation={false}
        creditContainer={null}
        selectionIndicator={false}
        sceneModePicker={false}
        infoBox={false}
        shadows={false}
      >
        <ImageryLayer imageryProvider={map1set} />
      </Viewer>

      <Viewer
        ref={rfcs2}
        className="w-2/4 mr-auto"
        style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
        vrButton={false}
        homeButton={false}
        timeline={false}
        navigationHelpButton={false}
        baseLayerPicker={false}
        fullscreenButton={false}
        geocoder={false}
        imageryProvider={BLANK_IMAGERY_PROVIDER}
        scene3DOnly={true}
        animation={false}
        creditContainer={null}
        selectionIndicator={false}
        sceneModePicker={false}
        infoBox={false}
        shadows={false}
      >
        <ImageryLayer imageryProvider={map2set} />
      </Viewer>
    </div>
  );
};